import React, { useEffect, useCallback, useState, useMemo } from 'react'

import { useHistory, Redirect, useLocation } from 'react-router-dom'
import qs from 'query-string'

import { useSetCredentials, useCredentials } from './CredentialsProvider'
import { useSecurityConfig } from './SecurityConfigProvider'
import useTokenRefresh from '../hooks/useTokenRefresh'

import { isTokenValid } from '../services/token'
import { authenticateComplete, getCurrentUser } from '../services/api'

import { hasAccessToTheApplication } from '../services/validations'
import redirectService from '../services/redirect'
import { storageKey } from '../config'

type Props = {
  children: React.ReactNode
}

export const AuthenticationHandler: React.FC<Props> = ({ children }) => {
  const securityConfig = useSecurityConfig()
  const history = useHistory()
  const location = useLocation()

  const security = useCredentials()
  const setSecurity = useSetCredentials()
  const [error, setError] = useState<string | undefined>()

  useTokenRefresh()

  const tokenValid = isTokenValid(security.token || '')
  const mustCheckApplicationPermission = Boolean(securityConfig.application)

  const haveCredentials = Boolean(security.token) && Boolean(security.authentication)

  const tokenHasExpired = haveCredentials && !tokenValid

  const { sid: sessionId, jwt, ...queryParams } = qs.parse(window.location.search)

  const saveRedirect = () => {
    redirectService.saveRedirect(
      {
        pathname: location.pathname,
        search: location.search,
      },
      securityConfig
    )
  }

  const token = useMemo(() => {
    if (!securityConfig.enableImpersonation) return

    return jwt
  }, [jwt])

  const isHashHistory = window.location.hash !== ''

  const handleAuth = useCallback(() => {
    if (sessionId) {
      authenticateComplete(sessionId as string)
        .then(({ user, token, refreshToken }) => {
          setSecurity({ authentication: user, token, refreshToken }, 'local')
          setError(undefined)

          const redirectLocationFromCookie = redirectService.getRedirect()

          if (isHashHistory) {
            if (redirectLocationFromCookie) {
              const url = `${window.location.origin}/#${redirectLocationFromCookie.pathname}${redirectLocationFromCookie.search}`

              redirectService.deleteRedirect()

              window.location.href = url
            } else {
              window.location.href = `${securityConfig.redirectUri}`
            }
          }
        })
        .catch((e) => setError(e.message))

      return
    }

    if (token && typeof token === 'string') {
      getCurrentUser(token)
        .then((user) => {
          setSecurity({ authentication: user, token, refreshToken: null }, 'session')
          setError(undefined)

          if (isHashHistory) {
            window.location.href = `${securityConfig.redirectUri}`
          }
        })
        .catch((e) => {
          setError(e.message ?? 'invalid_token')
        })

      return
    }

    // ignore `location.search` and dependencies to avoid infinite loop.
    // eslint-disable-next-line
  }, [history, setSecurity, token, sessionId])

  useEffect(handleAuth, [])

  useEffect(() => {
    if (error) {
      localStorage.removeItem(storageKey)
      sessionStorage.removeItem(storageKey)
      saveRedirect()
      setSecurity(null, 'local')
    }
  }, [error])
  
  useEffect(() => {
    if (jwt) {
      localStorage.removeItem(storageKey)
      setSecurity(null, 'local')
    }
  }, [jwt])

  if (error) {
    return <Redirect to={`${securityConfig.loginPath}?error=${error}`} />
  }

  if (token) {
    // Wait until handleAuth function result
    return <>{securityConfig.loading}</>
  }

  if (tokenHasExpired) {
    localStorage.removeItem(storageKey)
    saveRedirect()

    return <Redirect to={`${securityConfig.loginPath}?error=session_expired`} />
  }

  if (!haveCredentials && !sessionId && !token) {
    saveRedirect()

    return <Redirect to={securityConfig.loginPath} />
  }

  if (sessionId && haveCredentials) {
    const redirectLocationFromCookie = redirectService.getRedirect()

    if (redirectLocationFromCookie) {
      return <Redirect to={redirectLocationFromCookie} />
    }

    return <Redirect to={`${securityConfig.successPath}${qs.stringify(queryParams)}`} />
  }

  if (sessionId) {
    return <>{securityConfig.loading}</>
  }

  if (
    mustCheckApplicationPermission &&
    !hasAccessToTheApplication(securityConfig.application, security.authentication)
  ) {
    return <Redirect to={`${securityConfig.loginPath}?error=no_application_permission`} />
  }

  return <>{children}</>
}
